// utils/resolveDriverRoute.ts
import { generatePath } from 'react-router-dom'
import {
  driverRoutesConfig,
  type DriverRouteConfigItem,
  type RouteMode,
} from '../routes-config'

export type ResolvedRoute = {
  id: string
  mode: RouteMode
  key: string
  path: string
  url: string | null
}

export type ResolveDriverRouteResult = {
  route: ResolvedRoute | null
  routeGroup: ResolvedRoute | null
  routeSingle: ResolvedRoute | null
}

const escapeRegex = (s: string): string =>
  s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

const pathToPrefixRegex = (pathTemplate: string): RegExp => {
  // Match template as prefix, allowing extra trailing segments in pathname
  const parts = pathTemplate.split('/').filter(Boolean)
  const regexParts = parts.map((p) =>
    p.startsWith(':') ? '[^/]+' : escapeRegex(p),
  )
  const base = `/${regexParts.join('/')}`
  return new RegExp(`^${base}(?:/.*)?$`)
}

const extractParamsFromPathname = (
  pathTemplate: string,
  pathname: string,
): Record<string, string> => {
  const templateParts = pathTemplate.split('/').filter(Boolean)
  const pathParts = pathname.split('/').filter(Boolean)

  const params: Record<string, string> = {}

  templateParts.forEach((part, index) => {
    if (part.startsWith(':') && pathParts[index]) {
      params[part.slice(1)] = pathParts[index]
    }
  })

  return params
}

const buildUrlSafe = (
  pathTemplate: string,
  paramsFromPathname: Record<string, string>,
  defaults: Record<string, string>,
): string | null => {
  try {
    return generatePath(pathTemplate, {
      ...defaults,
      ...paramsFromPathname, // pathname wins over defaults
    })
  } catch {
    return null
  }
}

const pickBestCandidate = (
  candidates: DriverRouteConfigItem[],
  mode: RouteMode,
): DriverRouteConfigItem | null => {
  if (candidates.length === 0) return null
  if (candidates.length === 1) return candidates[0]

  // Heuristic for legacy duplicates:
  // - group: prefer key containing "Group"
  // - single: prefer key NOT containing "Group"
  const preferred = candidates.find((c) =>
    mode === 'group' ? c.key.includes('Group') : !c.key.includes('Group'),
  )

  return preferred ?? candidates[0]
}

/**
 * Resolve:
 * - matches best (longest) prefix route for pathname
 * - pairs group/single deterministically by shared `id`
 * - builds urls via react-router's generatePath
 * - if any required param missing => url = null
 */
export const resolveDriverRoute = (
  pathname: string,
  defaults: Record<string, string> = {},
  config: DriverRouteConfigItem[] = driverRoutesConfig,
): ResolveDriverRouteResult => {
  try {
    const sorted = [...config].sort((a, b) => b.path.length - a.path.length)

    let matched: DriverRouteConfigItem | null = null
    for (const item of sorted) {
      if (pathToPrefixRegex(item.path).test(pathname)) {
        matched = item
        break
      }
    }

    if (!matched) {
      return { route: null, routeGroup: null, routeSingle: null }
    }

    const paramsFromPathname = extractParamsFromPathname(matched.path, pathname)

    const toResolved = (item: DriverRouteConfigItem): ResolvedRoute => ({
      id: item.id,
      mode: item.mode,
      key: item.key,
      path: item.path,
      url: buildUrlSafe(item.path, paramsFromPathname, defaults),
    })

    const sameId = config.filter((x) => x.id === matched!.id)

    const groupCandidate = pickBestCandidate(
      sameId.filter((x) => x.mode === 'group'),
      'group',
    )
    const singleCandidate = pickBestCandidate(
      sameId.filter((x) => x.mode === 'single'),
      'single',
    )

    return {
      route: toResolved(matched),
      routeGroup: groupCandidate ? toResolved(groupCandidate) : null,
      routeSingle: singleCandidate ? toResolved(singleCandidate) : null,
    }
  } catch (e) {
    return {
      route: null,
      routeGroup: null,
      routeSingle: null,
    }
  }
}
